import { Injectable } from '@angular/core';
import { I18nSelectors } from '@next/core-lib/i18n';
import { UserSelectors } from '@next/core-lib/user';
import { Store, select } from '@ngrx/store';
import { Observable, of, combineLatest } from 'rxjs';
import { map, catchError, filter, switchMap } from 'rxjs/operators';

import { SkuCatalogItem } from 'src/app/models/catalog-item.model';
import { ElasticApiService } from 'src/app/services/elastic-api.service';

export interface ReturnableEmptyProduct {
  id: number;
  externalId: string;
  title: string;
  unit: string;
  productType: string;
  isReturnAllowed: boolean;
  maxReturnableQuantity?: number;
  orderUnitQuantity?: number;
}

@Injectable({
  providedIn: 'root',
})
export class ReturnableEmptyProductsService {
  constructor(
    private readonly store: Store,
    private readonly elasticApiService: ElasticApiService,
  ) {}

  /**
   * Get all returnable empty products (non-paginated) filtered by MaterialType ZVRP
   * @param searchTerm Optional search term to filter products
   * @returns Observable of returnable empty products array
   */
  public getReturnableEmptyProducts(searchTerm?: string): Observable<ReturnableEmptyProduct[]> {
    return combineLatest([this.store.pipe(select(UserSelectors.selectLocation)), this.store.pipe(select(I18nSelectors.selectLocale))]).pipe(
      filter(([location, locale]) => !!location && !!location.salesAreas?.length && !!locale),
      switchMap(([location, locale]) => {
        const languageCode = locale.split('-')[0].toLowerCase();

        return this.elasticApiService.getEmptyReturnableSkuCatalog({
          keywords: searchTerm || '',
          language: languageCode,
          salesOrganization: location.salesAreas[0].salesOrganization,
          materialType: 'ZVRP',
        });
      }),
      map((skuItems) => {
        return skuItems.map((item) => this.mapSkuCatalogItemToReturnableEmptyProduct(item));
      }),
      catchError((error) => {
        console.error('Error getting returnable empty products from Elastic Search:', error);
        return of([]);
      }),
    );
  }

  /**
   * Maps a SkuCatalogItem from Elastic Search to ReturnableEmptyProduct
   */
  private mapSkuCatalogItemToReturnableEmptyProduct(item: SkuCatalogItem): ReturnableEmptyProduct {
    return {
      id: item.skuId || 0,
      externalId: item.externalSkuId,
      title: item.title,
      unit: item.orderingInformation?.orderUnit || 'PCS',
      productType: 'ZVRP',
      isReturnAllowed: true,
      orderUnitQuantity: item.quantity || 1,
    };
  }
}
